import { useCallback, useEffect, useMemo, useState } from 'react';
import { ACTIONS, EVENTS, STATUS, type EventData, type Props as JoyrideProps, type Step } from 'react-joyride';
import type { TourId } from '@sorrem/shared';
import { useActionBarTargets, useInputBindings, useIsScreenCovered, useMarksTourRunning, useTourProgress } from '@sorrem-client/ui/hooks';
import { formatRunHint } from '@sorrem-client/ui/shared/run-hint-text';
import { TourTooltip } from './TourTooltip';
import { TOURS, type TourStepDefinition } from './tours';

/**
 * How long a tour waits once it may start, so the panels it points at have
 * slid in and settled (`SlidePanel`) before the spotlight measures them.
 */
export const TOUR_SETTLE_MS = 700;

/** Above the HUD, modals and the loading screen, under the cutscene cover's notifications. */
const TOUR_Z_INDEX = 1300;

const TOUR_OPTIONS: JoyrideProps['options'] = {
    skipBeacon: true,
    // Only the tooltip's buttons move a tour on: a stray click or Esc never skips it.
    overlayClickAction: false,
    dismissKeyAction: false,
    buttons: ['back', 'skip', 'primary'],
    zIndex: TOUR_Z_INDEX,
    overlayColor: 'rgba(0, 0, 0, 0.55)',
    spotlightRadius: 6,
    // A step whose mark leaves the page while the tour runs is passed over after this long.
    targetWaitTimeout: 1500,
};

export interface TourState {
    /** Whether the tour is on screen. */
    isRunning: boolean;
    /** Props for the controlled `Joyride` that shows it. */
    joyrideProps: JoyrideProps;
}

function isOnPage(step: TourStepDefinition): boolean {
    return step.target === undefined || document.querySelector(`[data-tour="${step.target}"]`) !== null;
}

function toJoyrideStep(step: TourStepDefinition, fillKeys: (text: string) => string): Step {
    return {
        target: step.target === undefined ? 'body' : `[data-tour="${step.target}"]`,
        placement: step.target === undefined ? 'center' : step.placement ?? 'auto',
        title: step.title,
        content: fillKeys(step.body),
    };
}

/**
 * One guided tour (#234), driven in controlled mode: it starts once
 * `isEnabled` has held for `TOUR_SETTLE_MS`, at the step the account left it
 * at, showing (and counting) only the steps whose marks are on the page then, steps aside while a cutscene or vision covers the screen, saves each
 * step it reaches, and is done for good once finished or skipped.
 */
export function useTour(tourId: TourId, isEnabled: boolean): TourState {
    const definition = TOURS[tourId];
    const progress = useTourProgress(tourId, definition.version);
    const isScreenCovered = useIsScreenCovered();
    const { bindings } = useInputBindings();
    const { targets: actionBar } = useActionBarTargets();
    // The tour's own indices of the steps it shows, read once it settles; null until then.
    const [shownSteps, setShownSteps] = useState<readonly number[] | null>(null);
    // An index into `shownSteps`.
    const [stepIndex, setStepIndex] = useState<number | null>(null);

    const isAvailable = isEnabled && progress.isLoaded && !progress.isDone;
    useEffect(() => {
        if (!isAvailable) {
            // Read again when it comes back, resuming from its saved step.
            setShownSteps(null);
            setStepIndex(null);
            return undefined;
        }
        const timer = window.setTimeout(() => {
            setShownSteps(definition.steps.flatMap((step, index) => (isOnPage(step) ? [index] : [])));
        }, TOUR_SETTLE_MS);
        return (): void => window.clearTimeout(timer);
    }, [definition.steps, isAvailable]);

    // Where it starts is read once, when it first may: the first shown step from where it was left. Later saves are this tour's own.
    const { startStep, finish, saveStep } = progress;
    useEffect(() => {
        if (shownSteps === null || stepIndex !== null) {
            return;
        }
        if (shownSteps.length === 0) {
            finish();
            return;
        }
        const resumeAt = shownSteps.findIndex((index) => index >= startStep);
        setStepIndex(resumeAt === -1 ? shownSteps.length - 1 : resumeAt);
    }, [finish, shownSteps, startStep, stepIndex]);

    const isRunning = isAvailable && shownSteps !== null && !isScreenCovered && stepIndex !== null;
    useMarksTourRunning(isRunning);

    const steps = useMemo(
        () => definition.steps
            .filter((_, index) => shownSteps?.includes(index) ?? false)
            .map((step) => toJoyrideStep(step, (text) => formatRunHint(text, bindings, actionBar))),
        [actionBar, bindings, definition.steps, shownSteps],
    );

    const handleEvent = useCallback((data: EventData): void => {
        if (data.action === ACTIONS.SKIP || data.status === STATUS.SKIPPED || data.status === STATUS.FINISHED) {
            finish();
            return;
        }
        if (data.type !== EVENTS.STEP_AFTER && data.type !== EVENTS.TARGET_NOT_FOUND) {
            return;
        }
        // Back from the first step has nowhere to go but on.
        const next = data.action === ACTIONS.PREV && data.index > 0 ? data.index - 1 : data.index + 1;
        if (next >= steps.length) {
            finish();
            return;
        }
        setStepIndex(next);
        saveStep(shownSteps?.[next] ?? next);
    }, [finish, saveStep, shownSteps, steps.length]);

    return {
        isRunning,
        joyrideProps: {
            steps,
            run: isRunning,
            stepIndex: stepIndex ?? 0,
            continuous: true,
            onEvent: handleEvent,
            tooltipComponent: TourTooltip,
            options: TOUR_OPTIONS,
        },
    };
}
